/**
 * Compares two strings in a base-sensitive manner.
 * "réservé" and "RESERVE" will be considered equal
 */
const baseCollator = new Intl.Collator(undefined, { sensitivity: "base" });
export const baseSensitiveCompare = (a: string, b: string): number => baseCollator.compare(a, b);

/**
 * Returns a Promise that resolves after the specified number of milliseconds.
 * Useful for adding delays in async functions.
 * @param ms The number of milliseconds to sleep
 * @param signal Optional AbortSignal to cancel the sleep early; the promise rejects with the signal's reason
 */
export const sleep = (ms: number, signal?: AbortSignal): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason);
      return;
    }

    const timeoutId = setTimeout(resolve, ms);

    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timeoutId);
        reject(signal.reason);
      },
      { once: true }
    );
  });
};

export type MaybeArray<T> = T | T[];

/**
 * Converts a value to an array if it is not already an array.
 * @param maybeArray The value to convert to an array
 * @returns An array of the value
 */
export const convertToArray = <T>(maybeArray?: MaybeArray<T | null | undefined> | null): T[] => {
  if (!maybeArray) return [];
  const arr = Array.isArray(maybeArray) ? maybeArray : [maybeArray];
  return arr.filter(notNullish);
};

/** If the value is not null or undefined, then return true */
export const notNullish = <T>(value: T | null | undefined | void): value is T => value != null;

/** Flatmap with unique values - has a callback for equality checking */
export const uniqueFlatMap = <T, R = T>(arrays: T[][], callback?: (element: T) => R): T[] => {
  const uniqueValues = new Set<R>();
  const result: T[] = [];

  arrays.forEach((array) => {
    array.forEach((element) => {
      // Determine the value for uniqueness check
      const checkValue = callback ? callback(element) : (element as unknown as R);

      if (!uniqueValues.has(checkValue)) {
        uniqueValues.add(checkValue);
        result.push(element); // Push the original element
      }
    });
  });

  return result;
};

/**
 * Recursively converts an object into an array of paths to the leaves of the object.
 *
 * Example:
 *  const obj = {
 *    a: {  b: 1, c: 2 },
 *    d: { e: { f: 3 } },
 *    g: 4,
 *  };
 *
 *  extractPaths(obj) => ["a.b", "a.c", "d.e.f", "g"]
 */
export const extractPaths = (obj: Record<string, $TSFixMe>, currentPath = "", delimiter = "."): string[] => {
  let paths: string[] = [];

  for (const key in obj) {
    const newPath = currentPath ? `${currentPath}${delimiter}${key}` : key;

    if (typeof obj[key] === "object" && obj[key] !== null && !(obj[key] instanceof Array)) {
      paths = paths.concat(extractPaths(obj[key], newPath, delimiter));
    } else {
      paths.push(newPath);
    }
  }

  return paths;
};

/**
 * Flatten's the keys of an object into a single string delimited by the provided delimiter.
 * */
export const flattenKeys = (obj: Record<string, $TSFixMe>, delimiter = "."): string[] => {
  return extractPaths(obj, "", delimiter);
};

export const hasProp = (obj: unknown, prop: PropertyKey): boolean => {
  return Object.prototype.hasOwnProperty.call(obj, prop);
};

// Re-export utility functions from string-utils
export {
  formatToTitleCase,
  camelToKebabCase,
  kebabToCamelCase,
  truncate,
  sanitizeAlphanumeric,
  deparameterizeCapitalizeFirstLetter,
} from "./string-utils";

// Re-export utility functions from date-utils
export {
  LUXON_WEEKDAY_BY_WEEK_START_DAY,
  configureBusinessCalendar,
  isMoreThanBusinessDaysAfter,
  getBusinessDaysSince,
  getBusinessDaysBetween,
  getFormattedDatePlusBusinessDays,
  getDateMinusBusinessDays,
  getClockInAndOut,
} from "./date-utils";

// Re-export utility functions from fringe-utils
export {
  getTotalPackageRate,
  isFringeDeduction,
  getOtAdjustedFringeAmount,
  resolveBaseRatesForTeamMember,
} from "./fringe-utils";

// Re-export utility functions from phone-utils
export {
  isValidPhone,
  formatE164,
  formatPhoneNumber,
  parsePhone,
  formatPhoneDigitsOnlyNoCountry,
  resolveSmsPhoneForTeamMember,
} from "./phone-utils";
export type { ResolvedSmsPhone, SmsPhonePreference } from "./phone-utils";

// Re-export utility functions from email-utils
export { isValidEmail, normalizeEmail } from "./email-utils";

// Re-export utility functions from miter-guides-utils
export { constructGitBookGuidePath, constructMiterGuidePath } from "./miter-guides-utils";

// Re-export utility functions from team-member-status
export {
  getTeamMemberActiveStatus,
  isActiveTeamMember,
  teamMemberActiveStatusForageFilter,
  type TeamMemberActiveStatusData,
} from "./team-member-status";

// Re-export utility functions from team-member-utils
export {
  FAILED_TO_ONBOARD_TERMINATION_REASON,
  filterTeamMembersForAnalytics,
  getCurrentEmploymentStartDate,
  getLatestTeamMemberDismissal,
  getEnrollmentStartDate,
  getPolicyTenureDays,
  getTotalCompanyTenureDays,
  getUserFriendlyTmFieldName,
  hasPhantomDismissalForCurrentEmploymentPeriod,
  isDismissalInCurrentEmploymentPeriod,
  isTeamMemberCurrentlyDismissedForFailedOnboarding,
  isTrackingMissedDeductionsOnLeave,
  type TeamMemberDismissalLike,
  type TeamMemberForEmploymentPeriod,
  type TeamMemberRehireLike,
  withResolvedLeaveTracking,
} from "./team-member-utils";

// Re-export utility functions from custom-field-utils
export {
  getCustomFieldSelectOptionValues,
  getCustomFieldStringValue,
  isRateDifferentialTriggerCustomField,
} from "./custom-field-utils";

// Re-export utility functions from offer-letter-utils
export {
  formatOfferLetterValue,
  getOfferLetterSmartFieldValueFormat,
  replaceSmartFieldTokens,
} from "./offer-letter-utils";

// Re-export utility functions from grouped-timesheet
export {
  formatGroupedTimesheetDateLabel,
  formatGroupedTimesheetDateRange,
  getGroupedTimesheetEntityName,
  getGroupedTimesheetStatus,
} from "./grouped-timesheet";

export { UnionFind } from "./union-find";

export * from "./bank-account-utils";
export * from "./expense-reimbursement-utils";
export * from "./integration-connection-utils";
export * from "./install-billing-schedule";
export * from "./billing-item-scope";
export * from "./net-pay-splits";
export * from "./safety-defaults";
export * from "./form-utils";
export * from "./tenure-occurrence-utils";
export * from "./missed-deductions-report-utils";
